import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { TableCell } from '@anytag/shared-atoms';
import { formatPrice } from '@anytag/shared-utils';
import { TiktokAdAccountInfo, TiktokAdAccountStatus } from '@anytag/graphql';
import { ColumnDef } from '@tanstack/react-table';
import { Badge, BadgeProps } from '@anyui/core';
import { TransferModal } from './TransferModal';

export interface useBudgetAllocationTableProps {
  prepaidBalance: number;
}

const getStatusColumnProps = (status: TiktokAdAccountStatus, t: TFunction): BadgeProps => {
  switch (status) {
    case TiktokAdAccountStatus.VERIFIED:
      return { children: t('Option.Approved'), color: 'green' };
    case TiktokAdAccountStatus.UNVERIFIED:
      return { children: t('Option.Not Approved'), color: 'red' };
    case TiktokAdAccountStatus.DISCONNECT:
    default:
      return { children: t('Option.Disconnected'), color: 'zinc' };
  }
};

export const useBudgetAllocationTable = ({ prepaidBalance }: useBudgetAllocationTableProps) => {
  const { t } = useTranslation();

  const columns: ColumnDef<TiktokAdAccountInfo>[] = [
    {
      accessorKey: 'name',
      header: t<string>('HeaderColumn.Ad Account Name'),
      cell: ({ row }) => <TableCell.Text value={row.original.name} />
    },
    {
      accessorKey: 'id',
      header: t<string>('HeaderColumn.Ad Account ID'),
      cell: ({ row }) => <TableCell.Text value={row.original.id} />
    },
    {
      accessorKey: 'status',
      header: t<string>('HeaderColumn.Status'),
      cell: ({ row }) => <Badge {...getStatusColumnProps(row.original.status, t)} />
    },
    {
      accessorKey: 'balanceAmount',
      header: t<string>('HeaderColumn.Balance'),
      cell: ({ row }) => (
        <TableCell.Number
          unit={row.original.currencyId}
          className="justify-start"
          value={formatPrice(row.original.balanceAmount, row.original.currencyId)}
        />
      )
    },
    {
      accessorKey: 'allocation',
      header: t<string>('HeaderColumn.Allocation'),
      cell: ({ row }) => <TransferModal adAccount={row.original} prepaidBalance={prepaidBalance} />
    }
  ];

  return { columns };
};
